import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { identifyEmpresa } from '../lib/posthog';
import { fetchHotSaleCoupon } from '../lib/hotSaleCoupon';
import { cacheOwner, getCached, setCached, CACHE_KEYS } from '../utils/sessionCache';

/**
 * Estado de cuenta del usuario para el shell nuevo — ADR-2026-07-31-shell-nuevo-datos-reales.
 *
 * El shell de `/inicio` corría con constantes: nombre "Maxi Cris", plan "gratis",
 * 0 créditos y 50% de descuento fijo. Eso alcanzaba mientras era un preview de
 * dev; ahora que es la interfaz de la plataforma, tiene que leer lo mismo que lee
 * el Sidebar de producción, y de las MISMAS tablas — si no, la sidebar vieja y la
 * nueva le dirían al mismo usuario dos planes distintos.
 *
 * Fuentes (idénticas a `components/Sidebar.tsx`):
 *  - `auth.getUser()` → id de sesión.
 *  - `profiles` → `name`, `email`, `id_company`.
 *  - `company_profiles` → `plan`, `imagenes_restantes`, `max_fotos_mes`.
 *  - `lib/hotSaleCoupon` → el mismo cupón invisible que aplica el catálogo, para
 *    que la barra de créditos no prometa un descuento distinto del que se cobra.
 *
 * NO se reimplementa el fetch del Sidebar ni se lo extrae: el Sidebar sigue
 * montándose en mobile y en el shell viejo, y moverle el fetch a este hook lo
 * ataría a una pantalla que en mobile no existe. Lo que sí se comparte es la
 * cache de sesión (`CACHE_KEYS.USER_PROFILE` / `COMPANY_DATA`), así el shell nuevo
 * no dispara una segunda ronda de queries por las mismas filas.
 */

export interface CuentaAtlas {
  /** `profiles.name`. `null` mientras carga o si la fila no lo tiene. */
  nombreCompleto: string | null;
  /** `profiles.email`, con el mail de la sesión como respaldo. */
  email: string | null;
  /** Empresa del usuario — la necesitan las creaciones recientes. */
  idCompany: string | null;
  /** `company_profiles.plan`. `'gratis'` es el default de la app. */
  plan: string;
  /** `company_profiles.imagenes_restantes` — el saldo de créditos. */
  creditos: number;
  /**
   * `company_profiles.max_fotos_mes` — la capacidad del plan, denominador del
   * umbral del 5% de `lib/creditsStatus`. `null` si la fila no la trae: en ese
   * caso el consumidor cae a `CAPACIDAD_POR_PLAN`.
   */
  capacidad: number | null;
  /**
   * Descuento vigente en % entero (ej. `50`), o `null` si no hay cupón activo —
   * y entonces la barra no promete ninguno.
   */
  descuento: number | null;
  /**
   * `company_profiles.dev_tester` — ADR-2026-08-20-la-sidebar-nueva-no-miraba-permisos.
   *
   * La fila ya lo traía (se lee para agrupar la analítica); lo que faltaba era
   * exponerlo. Lo necesita la sidebar del shell para gatear los accesos internos
   * igual que lo hace `components/Sidebar.tsx`. NO agrega una query.
   *
   * `false` mientras carga, que es el default seguro: un acceso interno no aparece
   * hasta que la fila confirma que corresponde.
   */
  devTester: boolean;
  /**
   * `company_profiles.grupo_testing` — el grupo de testeo ('A' / 'B' / `null`), que
   * es con lo que `lib/userGroups` decide qué experiencia de estudio se ve.
   */
  grupoTesting: string | null;
  /** `true` hasta que terminó la primera ronda de queries. */
  cargando: boolean;
}

const INICIAL: CuentaAtlas = {
  nombreCompleto: null,
  email: null,
  idCompany: null,
  plan: 'gratis',
  creditos: 0,
  capacidad: null,
  descuento: null,
  devTester: false,
  grupoTesting: null,
  cargando: true,
};

/** Lo que se cachea de `profiles` — el mismo shape que guarda el Sidebar. */
interface PerfilCacheado {
  id: string;
  name: string | null;
  email: string | null;
  id_company: string | null;
}

/** Lo que se cachea de `company_profiles`. */
interface EmpresaCacheada {
  plan: string | null;
  imagenes_restantes: number | null;
  max_fotos_mes: number | null;
  // Los cuatro de abajo no los muestra el shell: son para agrupar la analítica por
  // empresa — ver `identifyEmpresa` más abajo.
  nombre: string | null;
  pais: string | null;
  dev_tester: boolean | null;
  grupo_testing: string | null;
}

export function useCuentaAtlas(): CuentaAtlas {
  const [estado, setEstado] = useState<CuentaAtlas>(INICIAL);

  const cargar = useCallback(async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setEstado((s) => ({ ...s, cargando: false }));
        return;
      }

      /**
       * ¿La cuenta que estamos cargando sigue siendo la que está logueada?
       *
       * Esta función tiene tres `await` encadenados (perfil, empresa, cupón) y la
       * cuenta puede cambiar en el medio SIN que el shell se desmonte: pasa desde
       * otra pestaña o con un login sobre una sesión viva — los mismos dos casos
       * que documenta el listener de `App.tsx`. Sin esto, la corrida vieja seguía
       * hasta el final y publicaba los datos de la cuenta ANTERIOR: la empresa a
       * PostHog, su plan como semilla en `localStorage` (que es con lo que
       * `InterfazV2` y `Modos` gatean en el primer render) y todo el estado del
       * shell, `devTester` incluido — o sea, también sus accesos internos.
       *
       * `cacheOwner()` es la misma fuente que ya mantienen `App.tsx` y
       * `lib/cerrarSesion`, así que no hay un segundo registro de "quién está
       * logueado" que se pueda desincronizar. Es confiable acá porque el shell no
       * llega a montar antes: `App.tsx` no renderiza las rutas hasta
       * `setLoading(false)`, que corre DESPUÉS del primer `setCacheOwner`.
       */
      const vigente = () => cacheOwner() === user.id;
      /**
       * Se sale a los defaults y NO a lo que hubiera antes (`...s`): si una carga
       * previa dejó los datos de la cuenta anterior en pantalla, conservarlos es
       * exactamente el bug. Es el mismo degradado que el `catch` de abajo —
       * plan gratis y 0 créditos— y sobre todo apaga `cargando`, para no dejar el
       * shell en un skeleton eterno.
       */
      const abandonar = () => setEstado({ ...INICIAL, cargando: false });

      // `profiles` cambia poco; la cache de sesión evita repetir la query en cada
      // montaje del shell (que ocurre al entrar y al volver de una pantalla
      // full-screen).
      let perfil = getCached<PerfilCacheado>(CACHE_KEYS.USER_PROFILE);
      if (!perfil) {
        const { data } = await supabase
          .from('profiles')
          .select('id, name, email, id_company')
          .eq('id', user.id)
          .maybeSingle();
        perfil = (data as PerfilCacheado | null) ?? {
          id: user.id,
          name: null,
          email: null,
          id_company: null,
        };
        setCached(CACHE_KEYS.USER_PROFILE, perfil, user.id);
      }

      let empresa: EmpresaCacheada | null = null;
      if (perfil.id_company) {
        // El saldo SÍ cambia solo (cada generación lo baja), así que la fila de
        // empresa no se cachea: se lee fresca en cada carga y además se
        // reescucha `atlas:credits-updated` más abajo. Cachearla haría que el
        // contador se quedara clavado hasta 5 minutos después de generar.
        const { data } = await supabase
          .from('company_profiles')
          .select(
            'plan, imagenes_restantes, max_fotos_mes, nombre, pais, dev_tester, grupo_testing',
          )
          .eq('id_company', perfil.id_company)
          .maybeSingle();
        empresa = (data as EmpresaCacheada | null) ?? null;
        // Antes de `identifyEmpresa`: mandar esta empresa ahora la pegaría contra
        // la identidad de PostHog de la cuenta NUEVA, ensuciando la analítica de
        // las dos.
        if (!vigente()) return abandonar();

        /**
         * Agrupa la analítica por empresa — ADR-2026-08-05-el-shell-nuevo-tambien-identifica-la-empresa.
         *
         * Mismo caso, y misma causa, que el refresco del cache del plan de acá abajo
         * (ADR-2026-08-03-el-shell-nuevo-refresca-el-cache-del-plan): esto lo hacía
         * SÓLO el `Sidebar` viejo, que en desktop el shell nuevo ya no monta. Lo trajo
         * `principal` con PostHog, después de que el rediseño se llevara esas rutas
         * fuera de `DashboardLayout`, así que nadie lo notó al mergear: quien entra por
         * computadora y se queda en las pantallas del rediseño —la home, Creaciones,
         * Marca— no quedaba agrupado bajo ninguna empresa, y todo el panel de PostHog
         * filtra por eso.
         *
         * Va acá y no en un efecto aparte para no repetir la consulta: es la única de
         * la app que ya trae la fila de empresa en el shell nuevo. Los cuatro campos
         * extra del `select` son exactamente los que manda el Sidebar, para que las dos
         * llamadas describan a la empresa igual.
         */
        identifyEmpresa(perfil.id_company, {
          name: empresa?.nombre,
          plan: empresa?.plan,
          pais: empresa?.pais,
          dev_tester: empresa?.dev_tester === true,
          grupo_testing: empresa?.grupo_testing,
        });
      }

      // Falla cerrado como el catálogo: cualquier problema deja `null` y la
      // barra no promete descuento (ver `lib/hotSaleCoupon`).
      const cupon = await fetchHotSaleCoupon();
      // Último corte, y el que más importa: abajo vienen las DOS escrituras que
      // salen del hook —la semilla del plan en `localStorage` y el estado del
      // shell— y ninguna se puede deshacer sola.
      if (!vigente()) return abandonar();

      /**
       * Refresca el cache del plan — ADR-2026-08-03-el-shell-nuevo-refresca-el-cache-del-plan.
       *
       * `atlas-user-plan` es la SEMILLA con la que `InterfazV2` y `Modos` gatean en el
       * primer render, antes de que llegue su propio fetch. Lo escribía sólo el
       * `Sidebar` viejo, que en el shell nuevo no se monta: quien entraba siempre por
       * desktop se quedaba con el plan del día que lo escribió, y un cambio de plan no
       * se veía hasta pasar por una pantalla del shell viejo o limpiar el navegador.
       *
       * Acá es el lugar: esta fila se lee FRESCA en cada carga (no se cachea, a
       * propósito, porque el saldo cambia solo), así que el cache queda al día sin una
       * consulta de más.
       */
      if (empresa?.plan) {
        try {
          localStorage.setItem('atlas-user-plan', empresa.plan);
        } catch {
          /* noop */
        }
      }

      setEstado({
        nombreCompleto: perfil.name ?? null,
        email: perfil.email ?? user.email ?? null,
        idCompany: perfil.id_company ?? null,
        plan: empresa?.plan ?? 'gratis',
        creditos: empresa?.imagenes_restantes ?? 0,
        capacidad: empresa?.max_fotos_mes ?? null,
        descuento: cupon ? Math.round((1 - cupon.multiplicador) * 100) : null,
        devTester: empresa?.dev_tester === true,
        grupoTesting: empresa?.grupo_testing ?? null,
        cargando: false,
      });
    } catch {
      // Silencioso y sin romper la pantalla: el shell degrada a los defaults
      // (plan gratis, 0 créditos) igual que hacía con las constantes.
      setEstado((s) => ({ ...s, cargando: false }));
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Mismo evento que ya escucha el Sidebar de producción: lo emite cada flujo de
  // generación al descontar. Sin esto el contador del shell nuevo quedaría
  // desfasado del de la sidebar vieja durante toda la sesión.
  useEffect(() => {
    const handler = () => void cargar();
    window.addEventListener('atlas:credits-updated', handler);
    return () => window.removeEventListener('atlas:credits-updated', handler);
  }, [cargar]);

  return estado;
}

export default useCuentaAtlas;
